import React, { useCallback, useMemo, useRef } from 'react';
import { Badge, Button } from 'antd';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useNavigate } from 'react-router-dom';

import { PLGrid } from '../../components/common/PLGrid';
import { GridResetButton } from '../../components/common/GridResetButton';
import ExportAgCSVButton from '../../components/common/ExportAgCSVButton';
import { getActionColumn } from '../../libs/getActionColumn';
import { serialNumberColumn } from '../../libs/getSerialNumberColumn';
import { useGridData } from '../../hooks/useGridData';
import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import { getAppUrlInfo } from '../../utils/urls';
import ImageViewerModal from '../../components/common/ImageViewerModal';

import { documentConfig } from './document.config';
import { Document } from './document.types';

const DocumentList: React.FC = () => {
  const gridRef = useRef<AgGridReact>(null);
  const { rowData, loading, refetch } = useGridData<Document>(documentConfig.api.fetchAll);
  const deleteHandler = useDeleteHandler();
  const navigate = useNavigate();
  const { apiUrl } = getAppUrlInfo();

  const getFileNames = useCallback((doc: Document): string[] => {
    if (Array.isArray(doc.fileNames) && doc.fileNames.length > 0) return doc.fileNames;
    if (doc.fileName) return [doc.fileName];
    return [];
  }, []);

  const imagePreviewColumn = useMemo<ColDef<Document>>(
    () => ({
      headerName: 'Preview',
      colId: 'preview',
      field: 'fileNames',
      minWidth: 95,
      maxWidth: 95,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const fileNames = getFileNames(params.data || {});
        if (!fileNames.length) return '-';
        const imageFileNames = fileNames.filter((name) => {
          const ext = String(name).split('.').pop()?.toLowerCase() || '';
          return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
        });
        if (!imageFileNames.length) {
          const firstFileUrl = `${apiUrl}/files/documents/${encodeURIComponent(fileNames[0])}`;
          return (
            <a href={firstFileUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
              Open
            </a>
          );
        }
        const imageUrls = imageFileNames.map((name) => `${apiUrl}/files/documents/${encodeURIComponent(name)}`);
        const firstImageName = imageFileNames[0];
        const firstImageUrl = imageUrls[0];
        const fileCount = fileNames.length;
        return (
          <ImageViewerModal
            imageUrls={imageUrls}
            trigger={
              <Badge
                count={fileCount}
                offset={[-24, 24]}
                style={{ backgroundColor: 'rgba(22, 119, 255, 0.58)', color: '#fff' }}
              >
                <img
                  src={firstImageUrl}
                  alt={firstImageName}
                  className="h-12 w-12 object-cover rounded border border-gray-200"
                />
              </Badge>
            }
          />
        );
      },
    }),
    [apiUrl, getFileNames]
  );

  const fileColumn = useMemo<ColDef<Document>>(
    () => ({
      headerName: 'File Name',
      field: 'fileNames',
      minWidth: 260,
      cellRenderer: (params: any) => {
        const fileNames = getFileNames(params.data || {});
        if (!fileNames.length) return '-';
        const firstFileName = fileNames[0];
        const firstFileUrl = `${apiUrl}/files/documents/${encodeURIComponent(firstFileName)}`;
        const imageUrls = fileNames
          .filter((name) => {
            const ext = String(name).split('.').pop()?.toLowerCase() || '';
            return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
          })
          .map((name) => `${apiUrl}/files/documents/${encodeURIComponent(name)}`);

        return (
          <div className="flex flex-col gap-1">
            <span className="text-gray-700">
              {firstFileName}
              {fileNames.length > 1 ? ` (+${fileNames.length - 1} more)` : ''}
            </span>
            <div className="flex gap-3">
              {imageUrls.length > 0 ? (
                <ImageViewerModal
                  imageUrls={imageUrls}
                  trigger={
                    <img
                      src={imageUrls[0]}
                      alt={firstFileName}
                      className="h-8 w-8 object-cover rounded border border-gray-200"
                    />
                  }
                />
              ) : (
                <a href={firstFileUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                  Open
                </a>
              )}
              <a href={firstFileUrl} download className="text-blue-600 hover:text-blue-800">
                Download
              </a>
            </div>
          </div>
        );
      },
    }),
    [apiUrl, getFileNames]
  );

  const actionColumn = useMemo(
    () =>
      getActionColumn<Document>({
        viewUrl: (row) => `${documentConfig.redirectPath}/view/${row._id}`,
        editUrl: (row) => `${documentConfig.redirectPath}/edit/${row._id}`,
        onDelete: (row) =>
          deleteHandler({
            id: row._id,
            entityName: documentConfig.entityName,
            deleteApi: documentConfig.api.delete,
            onSuccess: () => refetch(),
          }),
      }),
    [deleteHandler, refetch]
  );

  const columnDefs = useMemo(
    () => [serialNumberColumn, imagePreviewColumn, ...documentConfig.documentTableColumns, fileColumn, actionColumn],
    [actionColumn, fileColumn, imagePreviewColumn]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold m-0">{documentConfig.entityName} List</h2>
        <div className="flex items-center gap-2">
          <GridResetButton gridRef={gridRef} />
          <ExportAgCSVButton gridRef={gridRef} fileName={`${documentConfig.entityName}List`} skipColumns={['preview', 'actions']} />
          <Button type="primary" onClick={() => navigate(`${documentConfig.redirectPath}/create`)}>
            Create {documentConfig.entityName}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto">
        <PLGrid ref={gridRef} rowData={rowData} columnDefs={columnDefs} loading={loading} />
      </div>
    </div>
  );
};

export default DocumentList;
